import { EyeOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Popconfirm,
  Popover,
  Radio,
  Row,
  Select,
  Spin,
  Tooltip,
  message,
  DatePicker,
} from "antd";
import locale from 'antd/es/date-picker/locale/es_ES';
import moment from "moment";
import 'moment/locale/es';
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HasAccess } from "@permify/react-role";
import { handlers } from "../../../../helpers/helpers";
import { consultationService } from "../../../../services/medicalConsultation";
import useRecommendationsModal from '../../../components/hooks/useRecommendationsModal';
import useImportAnamnesisModal from '../../../components/hooks/useImportAnamnesisModal';
import ConclusionsModal from "../ConclusionsModal";
import ImportAnamnesisModal from "../ImportAnamnesisModal";
import config from "../../../../config";
import { getCookie } from '../../../../services/cookies'
import { defaultAnamnesis } from "../../constants/defaultAnamnesis";
import SignButton from "../SignButton";
import usePendingExams from "../../hooks/usePendingExams";

const { TextArea } = Input;
const { Option } = Select;

const Health = ({
  id,
  form,
  disableForm,
  servicesList,
  serviceIndex,
  onFinishConsultation,
  onPresignConsultation,
  cancelPresign,
  generateReport,
  setIsEditingMode,
  onFormPopulated,
  loadingConsultation,
  medicalConsultationDetails,
  patientFullName,
  unfinishedExams,
  approvedTextList,
  loading,
  countryCode,
  setConsultationStatusType,
  listReproved,
  isUnableToChoose,
  validatedExams,
  hasToShowPopOver,
  setStatus,
  patientData,
  oldReport,
  oldReportCountry,
  reevaluationState,
  countrySettings,
  serviceDetail,
  showConfirmReprovingValues,
  setShowConfirmReprovingValues,
  allowPendingConsultationSign,
  expirationDate,
  generatedPrescriptions,
  hasDeferredExamPrescription,
}) => {
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [pendingReasons, setPendingReasons] = useState();
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [showPresignPopconfirm, setShowPresignPopconfirm] = useState(false);
  const [resultState, setResultState] = useState();
  const [showReprovePopover, setShowReprovePopover] = useState(false);
  const [showAlteredStatePopover, setShowAlteredStatePopover] = useState(false);
  const [conclusionPosition, setConclusionPosition] = useState();
  const [resultConclusionText, setResultConclusionText] = useState("");
  const [isPrefilled, setIsPrefilled] = useState(false);
  const { t } = useTranslation();
  const roles = getCookie('roles');
  const isPermittedEvaluatorRole = roles.includes('doctor') && !roles.includes('medical_auditor');
  const isPermittedAuditorRole = roles.includes('doctor') && roles.includes('medical_auditor');
  const hasMultipleHealthServices = servicesList.filter(service => ['health', 'normal'].includes(service.medical_form_type.toLowerCase())).length > 1;
  const recommendatiosModal = useRecommendationsModal({
    t,
    service: servicesList[serviceIndex],
    patientFullName,
    countryCode,
    conclusionPosition,
    setConclusionText: setResultConclusionText,
    oldReport,
    oldReportCountry,
    medicalForm: form,
    countrySettings,
  })

  const importAnamnesisModal = useImportAnamnesisModal({
    t,
    admissionPatientId: id,
    serviceId: servicesList[serviceIndex]?.id,
    serviceType: servicesList[serviceIndex]?.type,
    servicesList,
    patientFullName,
    countryCode,
    healthForm: form,
    getAvailableAnamneses: consultationService.getAvailableAnamneses,
  });

  const fetchPendingReasons = useCallback(async () => {
    setLoadingReasons(true);
    try {
      const response = await consultationService.getPendingReasons({ department_id: 1 });
      setPendingReasons(response);
    } catch (error) {
      console.error(error);
      message.error(t('medicalConsultation.consultationEdit.errorFetchingPendingReasons'));
    } finally {
      setLoadingReasons(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPendingReasons();
  }, [fetchPendingReasons]);

  const mapForm = async () => {
    const selectedService = servicesList[serviceIndex];
    const repeatComment = JSON.parse(localStorage.getItem(`repeatCommentForAll-${id}`));
    const useDefaultAnamnesis = countrySettings.medicalConsultation.useDefaultAnamnesis[countryCode];
    const anamnesisValue = useDefaultAnamnesis ? defaultAnamnesis : selectedService.default_anamnesis;
    if (medicalConsultationDetails && selectedService.medical_consultation_id && (medicalConsultationDetails?.started_at || medicalConsultationDetails?.revaluation)) {
      if (!servicesList.some((service) => service.medical_consultation_id === null)) {
        localStorage.removeItem(`repeatCommentForAll-${id}`);
      }
      const medicalConsultationStatus = approvedTextList
        ?.find(approvedText => approvedText.id === medicalConsultationDetails.consultation_result_id);

      setStatus(medicalConsultationStatus)


      form.setFieldsValue({
        showDetail: medicalConsultationDetails["show_detail"],
        showConclusion: medicalConsultationDetails["show_conclusion"],
        detail: medicalConsultationDetails.detail || selectedService.description,
        pendingReasons: medicalConsultationDetails["pending_reasons"]?.filter(reason => reason != null),
        conclusion: medicalConsultationDetails["conclusion"],
        diagnostics: medicalConsultationDetails["diagnostics"]
          .filter(diagnostic => diagnostic.id !== null),
        ...((!oldReport || !oldReportCountry) && {
          indication: medicalConsultationDetails.indications,
          suggestion: medicalConsultationDetails.suggestions,
        }),
        comment: medicalConsultationDetails.comment || anamnesisValue,
        secondary_conclusion: medicalConsultationDetails.secondary_conclusion,
        status: medicalConsultationDetails.consultation_result_id,
        expirationDate: moment(medicalConsultationDetails.report_expiration_date, "YYYY-MM-DD"),
      });
      if (medicalConsultationDetails.consultation_result_id && approvedTextList) {
        const approvedText = approvedTextList.find((option) =>
          option.id === medicalConsultationDetails.consultation_result_id
        );
        setResultState(approvedText?.status);
      }
    } else {
      const date = await expirationDate({
        admission_patient_id: id,
        department_id: 1,
        service_id: selectedService.id,
        service_type: selectedService.type,
      });
      const notApplicableText = 'No aplica';
      const shouldPrefillWithNotApplicable = countrySettings.medicalConsultation.examConsultationPrefill?.[countryCode]
        && (selectedService?.type === 'Exam' || medicalConsultationDetails?.service_type === 'Exam');
      const evaluatedOption = approvedTextList?.find(option => option.status === 'evaluated');

      form.setFieldsValue({
        showDetail: true,
        showConclusion: true,
        comment: shouldPrefillWithNotApplicable ? notApplicableText : commentInterpolation(
          repeatComment,
          anamnesisValue,
        ),
        detail: shouldPrefillWithNotApplicable ? notApplicableText : selectedService.description,
        conclusion: shouldPrefillWithNotApplicable ? notApplicableText : '',
        ...(countrySettings.medicalConsultation.showIndicationsAndSuggestions[countryCode] && {
          indication: shouldPrefillWithNotApplicable ? notApplicableText : 'Aptitud preliminar, sujeta a evaluación y validación a cargo de medico responsable de la vigilancia de salud de los trabajadores de la empresa (según RM 312-2011/MINSA numeral 6.4.5 y 6.4.7).',
          suggestion: shouldPrefillWithNotApplicable ? notApplicableText : '',
        }),
        ...(shouldPrefillWithNotApplicable && evaluatedOption && {
          status: evaluatedOption.id,
        }),
        expirationDate: moment(date, "YYYY-MM-DD"),
      });
      if (shouldPrefillWithNotApplicable && evaluatedOption) {
        setStatus(evaluatedOption);
        setResultState(evaluatedOption.status);
      }
      setIsPrefilled(shouldPrefillWithNotApplicable);
    }
    form.setFieldsValue({
      id: selectedService["medical_consultation_id"] ? selectedService["medical_consultation_id"] : null,
      serviceId: selectedService["id"],
      serviceType: selectedService["type"],
      admissionPatientId: parseInt(id),
    });
    onFormPopulated?.(form.getFieldsValue());
  }

  useEffect(() => {
    mapForm();
  }, [serviceIndex, medicalConsultationDetails, servicesList, id, form, approvedTextList]);

  const commentInterpolation = (repeatComment, comment) => {
    if (repeatComment) {
      const value = JSON.stringify(comment);
      const rest = value.split("\n").splice(4);
      return `${repeatComment}${rest.join("\n")}`;
    }

    return comment;
  };

  const saveText = (consultation) => {
    if (consultation?.revaluation) {
      return "Editar Reevaluación";
    }

    if (consultation && consultation["state"] === "readyToSign") {
      return "Editar";
    }

    return "Guardar";
  };

  const alignment = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };

  const handleResultState = (event) => {
    const status = approvedTextList.find((option) =>
      option.id === event.target.value
    );
    form.setFieldsValue({ pendingReasons: undefined });
    setStatus(status);
    let preText = '';
    const consultationText = status.conclusion_text ?? '';
    preText = servicesList[serviceIndex].child_company_id ? "" : `${status.text.toUpperCase()} \n${consultationText}\n`;
    if (status.status === 'approved') {
      const defaultText = servicesList[serviceIndex].approved_text || '';
      form.setFieldsValue({
        conclusion: `${preText}${defaultText}`
      });
      setShowReprovePopover(false)

      if (servicesList[serviceIndex].is_provider) {
        form.setFieldsValue({
          secondary_conclusion: servicesList[serviceIndex].secondary_approved_text
        });
      }

    } else if (status.status === 'reproved') {
      const defaultText = servicesList[serviceIndex].reprobate_text || '';
      form.setFieldsValue({
        conclusion: `${preText}${defaultText}`
      });
      setShowReprovePopover(servicesList[serviceIndex].type === "Battery");

      if (servicesList[serviceIndex].is_provider) {
        form.setFieldsValue({
          secondary_conclusion: servicesList[serviceIndex].secondary_disapproved_text
        });
      }
    } else if (status.status === 'pending') {
      form.setFieldsValue({ conclusion: "" });
      form.setFieldsValue({ secondary_conclusion: "" });
      setShowReprovePopover(false);
    } else if (status.status === 'abandoned') {
      form.setFieldsValue({ conclusion: `${status.conclusion_text}` });
    } else if (status.status === 'reproved_temporally' && (oldReport && oldReportCountry)) {
      const defaultText = servicesList[serviceIndex].reprobate_text || '';
      form.setFieldsValue({
        conclusion: `${preText}${defaultText}`
      });
    } else {
      setShowReprovePopover(false);

      form.setFieldsValue({
        conclusion: preText
      });
    }

    if (status.status === 'reproved_temporally' && servicesList[serviceIndex].is_provider) {
      form.setFieldsValue({
        secondary_conclusion: servicesList[serviceIndex].secondary_disapproved_text
      });
    }
    setResultState(status.status);
    setConsultationStatusType(status.status_type);
  };

  const downloadUnSignedReport = async (id) => {
    setLoadingDownload(true);
    try {
      const response = await generateReport(id, undefined, false, false);
      handlers.base642Open(response["pdf"]);
    } catch (error) {
      console.error(error);
      message.error({ content: "Error al descargar informe", style: { marginTop: "35vh", fontSize: 16, }, });
    } finally {
      setLoadingDownload(false);
    }
  };

  const downloadSignedReport = async (action, id, type) => {
    try {
      setLoadingDownload(true);
      const fileName = `${patientFullName}_${patientData.document_number}_${id}.pdf`
      await handlers.downloadSignedReport({
        id,
        type,
        countryCode,
        fileName,
        action,
      })
    } catch (error) {
      console.error(error);
      message.error({ content: "Error al descargar informe", style: { marginTop: "35vh", fontSize: 16, }, });
    } finally {
      setLoadingDownload(false);
    }
  }

  const fieldsMessages = {
    'pendingReasons': t('medicalConsultations.healthForm.pendingReasonsMessage'),
    'comment': t('medicalConsultations.healthForm.commentMessage'),
    'conclusion': t('medicalConsultations.healthForm.conclusionMessage'),
    'secondary_conclusion': t('medicalConsultations.healthForm.secondaryConclusionMessage'),
    'detail': t('medicalConsultations.healthForm.detailMessage'),
    'expirationDate': t('medicalConsultations.healthForm.expirationDate')
  };

  const rulesForRequiredFields = (field) => {
    return {
      required: true,
      message: fieldsMessages[field],
    };
  };

  const validateCompletedReport = () => {
    form.validateFields()
      .then(value => {
        form.setFieldsValue({ sign: true });
      });
  };

  const pendingExams = usePendingExams(unfinishedExams, medicalConsultationDetails, disableForm);

  const presignEnabled = useMemo(() => {
    if (!countrySettings.medicalConsultation.allowPresign[countryCode]) return false;
    if ((config?.restrictedCompaniesSignIds?.split(',') || [])?.includes(patientData?.company_id?.toString()) && !roles.includes('medical_auditor')) return false;
    const pendingReasons = medicalConsultationDetails?.pending_reasons;
    const isAlreadyPresigned = !!medicalConsultationDetails?.presign_state;
    const haveOnlyEnabledReasons = pendingReasons?.every(reason => ['laboratory', 'rx_oit'].includes(reason));
    const hasAlreadyReprovedExams = validatedExams?.some(exam =>
      (exam.exam_type !== 'laboratory') && (!exam.allow_presign) && ((exam.procedures?.some(procedure => (
        procedure.result_type?.includes('reproves')
        || !procedure.result_type
      )))
        ||
        (exam.measurements?.some(measurement => (
          measurement.result_type?.includes('reproves')
          || !measurement.result_type
        ))))
    )
    const allUnfinishedExamsAreLaboratory = unfinishedExams?.every(exam => exam.exam_type === 'laboratory' || exam.code === 'OIT');
    const preSignBatteries = config.presignBatteries?.split(',');
    const isBatteryPresign = preSignBatteries?.includes(servicesList[serviceIndex]?.service_code);
    return allUnfinishedExamsAreLaboratory && haveOnlyEnabledReasons && !isAlreadyPresigned && isBatteryPresign && !hasAlreadyReprovedExams
  }, [countryCode, countrySettings.medicalConsultation.allowPresign, medicalConsultationDetails?.pending_reasons, medicalConsultationDetails?.presign_state, patientData?.company_id, roles, serviceIndex, servicesList, unfinishedExams, validatedExams]);

  const conclusionBlur = (event) => {
    if (countryCode === 'CL') {
      const textAreaRef = event.target;

      if (textAreaRef.value !== '') {
        setConclusionPosition(textAreaRef.selectionStart);
      }
    }
  };

  const handleConclusionInput = (event) => {
    setResultConclusionText(event.target.value);
  };

  const isCompanyAuditorSignerEnabled = () => {
    return countrySettings?.medicalConsultation?.auditorSignature[countryCode]
      || (config?.restrictedCompaniesSignIds?.split(',') || [])?.includes(patientData?.company_id?.toString());
  };

  const isAuditorInfoEnabled = ((isPermittedAuditorRole && isCompanyAuditorSignerEnabled()) || !isCompanyAuditorSignerEnabled());

  const normalStates = [
    'normal',
    'negativo',
    'aprobado',
    'realizado',
    'sin evidencia de neumoconiosis',
    'no reactivo',
    'sin antecedentes de salud que contraindiquen la exposición a hiperbaría'
  ]

  const alteredStateFilterCondition = service => service.department_id === 1 && (service.exam_state_result?.trim()) && !normalStates.includes(service.exam_state_result.toLowerCase().trim())

  const handleAlteredStatePopover = (open) => {
    if (serviceDetail.some(alteredStateFilterCondition)) {
      setShowAlteredStatePopover(open);
    }
  }

  const renderAltereStatePopoverContent = () => {
    const filteredServices = serviceDetail.filter(alteredStateFilterCondition)
    if (filteredServices.length > 0) {
      return (
        <div>
          {t('medicalConsultations.consultationEdit.alteredStatePopoverContent')}
          {filteredServices.map((service, index) => (
            <p key={index}>{service.name}: <strong>{service.exam_state_result}</strong></p>
          ))}
        </div>
      )
    }
  }

  const showPopconfirm = () => {
    setShowPresignPopconfirm(true);
  };

  const handleClosePopconfirm = () => {
    setShowPresignPopconfirm(false);
  };

  const handlePresign = () => {
    handleClosePopconfirm();
    onPresignConsultation();
  }

  const warningReprovedDrugs = countrySettings.medicalConsultation.showDrugsAlert[countryCode] && (resultState === "reproved" || resultState === "reproved_temporally") && serviceDetail?.some(service => service?.has_drugs)

  useEffect(() => {
    const currentSuggestions = form.getFieldValue('suggestion');

    if (!currentSuggestions?.trim()) {
      const extractedSuggestions = validatedExams.flatMap((exam) => [
        ...(exam.procedures || []),
        ...(exam.measurements || [])
      ]).flatMap((item) => item.suggestions || []);

      if (extractedSuggestions.length > 0) {
        const uniqueSuggestions = [...new Set(extractedSuggestions.map(suggestion => suggestion.trim()))];

        form.setFieldsValue({
          suggestion: uniqueSuggestions.join('\n'),
        });
      }
    }
  }, [validatedExams, form]);

  return (
    <Fragment>
      <Form
        form={form}
        onFinish={onFinishConsultation}
        onFieldsChange={() => setIsEditingMode(true)}
      >
        <Form.Item name="id" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="serviceId" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="serviceType" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="admissionPatientId" hidden>
          <Input type="hidden" />
        </Form.Item>

        <Row gutter={50}>
          <Col>
            <Row gutter={10} align="middle">
              <Col style={alignment}>
                <Form.Item
                  name="showDetail"
                  valuePropName="checked"
                  className="mb-0"
                >
                  <Checkbox disabled={disableForm} />
                </Form.Item>
              </Col>
              <Col>
                <p className="mb-0">{t('medicalConsultations.healthForm.showConsultation')}</p>
              </Col>
            </Row>
          </Col>
          <Col>
            <Row gutter={10} align="middle">
              <Col style={alignment}>
                <Form.Item
                  name="showConclusion"
                  valuePropName="checked"
                  className="mb-0"
                >
                  <Checkbox disabled={disableForm} />
                </Form.Item>
              </Col>
              <Col>
                <p className="mb-0">{t('medicalConsultations.healthForm.showConclusions')}</p>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className={countrySettings.medicalConsultation.anamnesis.margin[countryCode]}>
          <Col lg={24} xs={24}>
            <p className={`bold ${countrySettings.medicalConsultation.anamnesis.titleMargin[countryCode]}`}>{t('medicalConsultations.healthForm.anamnesisAndComments')}</p>
          </Col>
          {/* {
            servicesList[serviceIndex]["default_anamnesis"]?.includes(
              "FUR"
            ) && (
              <Col lg={24} xs={24}>
                <Row gutter={10} align="middle">
                  <Col style={alignment}>
                    <a onClick={copyGeneralData} className="mb-0">
                      Copiar datos generales para todas las prestaciones
                    </a>
                  </Col>
                </Row>
              </Col>
            )} */}

          <Col lg={24} xs={24}>
            <Form.Item
              name="comment"
              rules={[
                rulesForRequiredFields('comment')
              ]}
              className='mb-0'
            >
              <TextArea
                id="anamnesisInput"
                className="cmt-input"
                style={{ height: countrySettings.medicalConsultation.anamnesis.textAreaSize[countryCode] }}
                disabled={disableForm || isPrefilled}
              />
            </Form.Item>
          </Col>
          {hasMultipleHealthServices && (
            <Col>
              <Button
                onClick={() => importAnamnesisModal.showModal()}
                className="cmt-button secondary"
                type="primary"
                size="small"
                disabled={disableForm}
              >
                {t('medicalConsultations.healthForm.importAnamnesis')}
              </Button>
            </Col>)
          }
        </Row>
        {
          countrySettings.medicalConsultation.detail.show[countryCode] &&
          <Row>
            <Col lg={24} xs={24}>
              <p className={`bold mt-30`}>{t('medicalConsultations.healthForm.physicalExam')}</p>
            </Col>
            <Col lg={24} xs={24}>
              <Form.Item
                name="detail"
                rules={[
                  rulesForRequiredFields('detail')
                ]}
              >
                <TextArea
                  id="physicalExamInput"
                  className="cmt-input"
                  style={{ height: 140 }}
                  disabled={disableForm || isPrefilled}
                />
              </Form.Item>
            </Col>
          </Row>
        }
        <Row>
          <Col lg={24} xs={24}>
            <p className={`bold ${countrySettings.medicalConsultation.status.titleMargin[countryCode]}`}>{t('medicalConsultations.healthForm.result')}</p>
          </Col >
          {
            showReprovePopover && countryCode === 'CL' && (
              <Col lg={24} xs={24}>
                <p className='reevaluation-message'>
                  Recuerde que para generar una <strong>reevaluación</strong> debe seleccionar el estado <strong> {reevaluationState?.text} + prescripción</strong>
                </p>
              </Col>
            )}
          <Col lg={24} xs={24}>
            <Spin spinning={loading.criterions} className='cmt-spin'>
              {
                (hasToShowPopOver() && !isUnableToChoose()) &&
                <Alert
                  message={<strong>{t('medicalConsultations.consultationEdit.statusAlteredDisclaimerTitle')}</strong>}
                  description={listReproved()}
                  type="info"
                  icon={<span className="icon-info-rounded" />}
                  showIcon
                />
              }
            </Spin>
          </Col>
          <Col lg={24} xs={24} className="consultation-status-container">
            <Row>
              <Col xxl={7} lg={countrySettings.medicalConsultation.status.colLgWidth[countryCode]} xs={24}>
                <Spin spinning={loading.approvedText}>
                  <Form.Item
                    name="status"
                    rules={[
                      rulesForRequiredFields('status')
                    ]}
                    className={countrySettings.medicalConsultation.status.formItemMargin[countryCode]}
                  >
                    {
                      <Radio.Group
                        className="cmt-radio-left"
                        onChange={handleResultState}
                        disabled={disableForm}
                      >
                        {
                          approvedTextList?.map((option) =>
                            ((
                              (countrySettings.medicalConsultation.medicalConsultationTracker[countryCode] && option.status === 'approved')
                              || (!countrySettings.medicalConsultation.medicalConsultationTracker[countryCode] && option.text === 'Apto')
                            )
                              && (hasToShowPopOver() && isUnableToChoose()))
                              ? <Popover
                                key={option.id}
                                title={
                                  <strong>
                                    {t('medicalConsultations.consultationEdit.statusReprovedDisclaimerTitle')}
                                  </strong>
                                }
                                trigger="click"
                                content={listReproved()}
                                overlayClassName="sidebar-tooltip"
                              >
                                <Radio
                                  value={option.id}
                                >
                                  {option.text}
                                </Radio>
                              </Popover>
                              : <Radio key={option.id} value={option.id}> {option.text} </Radio>
                          )
                        }
                      </Radio.Group>
                    }
                  </Form.Item>
                </Spin>
              </Col>
              {
                countrySettings.medicalConsultation.healthCustomExpirationDate[countryCode] &&
                <Col xs={24} lg={8} className='camo-container ml-auto'>
                  <Form.Item
                    name="expirationDate"
                    label="Fecha vigencia CAMO:"
                    rules={[
                      rulesForRequiredFields('expirationDate')
                    ]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                  >
                    <DatePicker
                      className="cmt-date-picker"
                      locale={locale}
                      disabled={disableForm}
                      format="DD-MM-YYYY"
                    />
                  </Form.Item>
                </Col>
              }
              <Col lg={12} xs={24}>
                {resultState === "pending" && (
                  <Form.Item
                    name="pendingReasons"
                    rules={[
                      rulesForRequiredFields('pendingReasons')
                    ]}
                  >
                    {loadingReasons ? (
                      <Spin className="custom-spinner" />
                    ) : (
                      <Select
                        className="cmt-select"
                        mode="multiple"
                        placeholder="Seleccione motivo/s"
                        style={{ width: "100%" }}
                        disabled={disableForm}
                      >
                        {
                          pendingReasons?.map((item) => {
                            return (
                              <Option key={item.key} value={item.key}>
                                {item.description}
                              </Option>
                            );
                          })}
                      </Select>
                    )}
                  </Form.Item>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
        <Row gutter={[0, 20]}>
          {
            countrySettings.medicalConsultation.conclusions.show[countryCode] &&
            <>
              <Col lg={24} xs={24}>
                <p className="bold mt-30 mb-0">{t('medicalConsultations.healthForm.conclusions')}</p>
              </Col>
              {warningReprovedDrugs && (
                <Col lg={24} xs={24}>
                  <Alert
                    message={<div dangerouslySetInnerHTML={{ __html: t('medicalConsultations.consultationEdit.drugsAlert', { status: approvedTextList.filter(item => item.status === resultState)[0]?.text }) }} />}
                    type="error"
                    className="drugs-alert-border"
                    icon={<span className="icon-alert-triangle-bold" />}
                    showIcon
                  />
                </Col>
              )}
              <Col lg={24} xs={24}>
                <Form.Item
                  name="conclusion"
                  className="mb-0"
                  rules={[
                    rulesForRequiredFields('conclusion')
                  ]}
                >
                  <TextArea
                    className={`cmt-input${resultConclusionText?.length >= 1600 && (!oldReport && oldReportCountry) ? '-error' : ''} ${warningReprovedDrugs ? 'drugs-alert-border' : ''}`}
                    style={{ height: 140 }}
                    disabled={disableForm}
                    onBlur={conclusionBlur}
                    onChange={(event) =>
                      handleConclusionInput(event)
                    }
                    maxLength={(!oldReport && oldReportCountry) && '1600'}
                  />
                </Form.Item>
              </Col>
              {
                (!oldReport && oldReportCountry) &&
                <Col lg={24} xs={24} className={`text-right conclusion-alert${resultConclusionText?.length >= 1600 ? '-error' : ''}`}>
                  Máximo 1.600 caracteres
                </Col>
              }
            </>
          }
          <Col lg={24} xs={24}>
            <Row gutter={15} className={countrySettings.medicalConsultation.indicationsSuggestionButton.margin[countryCode]}>
              <Col>
                <Button
                  onClick={() => recommendatiosModal.showModal({
                    type: "indication",
                    from: "conclusions",
                  })}
                  className="cmt-button secondary"
                  type="primary"
                  size="small"
                  disabled={disableForm}
                >
                  {t('medicalConsultations.healthForm.indications')}
                </Button>
              </Col>
              <Col>
                <Button
                  id="suggestionsButton"
                  onClick={() => recommendatiosModal.showModal({
                    type: "suggestion",
                    from: "conclusions",
                  })}
                  className="cmt-button secondary"
                  type="primary"
                  size="small"
                  disabled={disableForm}
                >
                  {t('medicalConsultations.healthForm.suggestions')}
                </Button>
              </Col>
            </Row>
          </Col>
          {
            (!oldReport || !oldReportCountry) &&
            <Col lg={24} xs={24}>
              <Col lg={24} xs={24}>
                <p className={`bold ${countrySettings.medicalConsultation.indications.title[countryCode]}`}>
                  {t('medicalConsultations.healthForm.indications')}
                </p>
              </Col>
              <Col lg={24} xs={24}>
                <Form.Item
                  name="indication"
                  className={countrySettings.medicalConsultation.indications.textAreaMargin[countryCode]}
                >
                  <TextArea
                    className="cmt-input"
                    style={{ height: countrySettings.medicalConsultation.indications.textAreaSize[countryCode] }}
                    disabled={isPrefilled}
                  />
                </Form.Item>
              </Col>
            </Col>
          }
          {
            (!oldReport || !oldReportCountry) &&
            <Col lg={24} xs={24}>
              <Col lg={24} xs={24}>
                <p className={`bold ${countrySettings.medicalConsultation.suggestion.title[countryCode]}`}>
                  {t('medicalConsultations.healthForm.suggestions')}
                </p>
              </Col>
              <Col lg={24} xs={24}>
                <Form.Item
                  name="suggestion"
                  className={countrySettings.medicalConsultation.suggestion.textAreaMargin[countryCode]}
                >
                  <TextArea
                    className="cmt-input"
                    style={{ height: countrySettings.medicalConsultation.suggestion.textAreaSize[countryCode] }}
                    disabled={isPrefilled}
                  />
                </Form.Item>
              </Col>
            </Col>
          }
        </Row >
        {
          servicesList[serviceIndex]?.is_provider &&
          ((servicesList[serviceIndex].type === "Battery" &&
            servicesList[serviceIndex].secondary_approved_text &&
            servicesList[serviceIndex].secondary_disapproved_text) ||
            servicesList[serviceIndex].type === "Exam") && (
            <Row>
              <Col lg={24} xs={24}>
                <p className="bold mt-30">{t('medicalConsultations.healthForm.secondaryConclusions')}</p>
              </Col>
              <Col lg={24} xs={24}>
                <Form.Item
                  name="secondary_conclusion"
                  rules={[
                    rulesForRequiredFields('secondary_conclusion')
                  ]}
                >
                  <TextArea
                    className="cmt-input"
                    style={{ height: 140 }}
                    disabled={disableForm}
                  />
                </Form.Item>
              </Col>
            </Row>
          )
        }
        {
          isCompanyAuditorSignerEnabled() &&
          <Row className={countrySettings.medicalConsultation.buttonTitleMargin[countryCode]}>
            {isPermittedEvaluatorRole ?
              <h2>{t('medicalConsultations.healthForm.medicalEvaluationTitle')}</h2>
              :
              <h2>{t('medicalConsultations.healthForm.medicalAuditionTitle')}</h2>
            }
          </Row>
        }

        <Row className="mt-0" gutter={[15, 10]}>
          <HasAccess
            roles={['super_usuario', 'doctor', 'direccion_medica']}
            permissions='save-medical-consultation'
          >
            <Col xxl={4} lg={6} xs={24}>
              <Form.Item name="save">
                <Popover
                  title={
                    <strong>
                      {t('medicalConsultations.consultationEdit.alteredStateTitle')}
                    </strong>
                  }
                  trigger="hover"
                  content={renderAltereStatePopoverContent()}
                  open={showAlteredStatePopover}
                  onOpenChange={handleAlteredStatePopover}
                  overlayClassName="sidebar-tooltip">
                  <Button
                    loading={loadingConsultation.save}
                    htmlType="submit"
                    onClick={() => form.setFieldsValue({ save: true })}
                    className="cmt-button primary"
                    type="primary"
                    size="large"
                    disabled={disableForm && disableForm}
                    id="saveButton"
                  >
                    {saveText(medicalConsultationDetails)}
                  </Button>
                </Popover>
              </Form.Item >
            </Col >
            {medicalConsultationDetails?.started_at &&
              <Fragment>
                <Col xxl={4} lg={6} xs={24}>
                  <Form.Item name="saveAndFinalize">
                    <Button
                      loading={loadingConsultation.saveAndFinalize}
                      htmlType="submit"
                      id="saveAndFinalize"
                      onClick={() =>
                        form.setFieldsValue({ saveAndFinalize: true })
                      }
                      className="cmt-button primary outline p-0"
                      type="primary"
                      size="large"
                      disabled={disableForm && disableForm}
                    >
                      Guardar y finalizar
                    </Button>
                  </Form.Item >
                </Col >
                {medicalConsultationDetails.done_at &&
                  <Col xxl={4} lg={6} xs={24}>
                    {presignEnabled ?
                      <Form.Item name="presign">
                        <Popconfirm
                          icon={<span className="icon-info-rounded presign-popconfirm-icon" />}
                          title={<div className="presign-popconfirm-title">
                            <strong>{t("medicalConsultations.healthForm.presignTitle")}</strong>
                            <br />
                            <p className="presign-popconfirm-description">
                              {t("medicalConsultations.healthForm.presignDescription")}
                            </p>
                          </div>}
                          open={showPresignPopconfirm}
                          onConfirm={handlePresign}
                          okButtonProps={{
                            className: "presign-popconfirm-button cmt-button primary",
                          }}
                          cancelButtonProps={{
                            className: "presign-popconfirm-button cmt-button primary outline",
                          }}
                          okText="Si"
                          cancelText="No"
                          onCancel={handleClosePopconfirm}
                        >
                          <Button
                            type="primary"
                            onClick={showPopconfirm}
                            size="large"
                            loading={loadingConsultation.presign}
                            className="cmt-button secondary outline">
                            {t('medicalConsultations.healthForm.presignButton')}
                          </Button>
                        </Popconfirm>
                      </Form.Item>
                      : medicalConsultationDetails?.presign_state === 'waiting_lab' ?
                        <Form.Item name="cancelPresign">
                          <Button
                            type="primary"
                            onClick={cancelPresign}
                            size="large"
                            loading={loadingConsultation.presign}
                            className="cmt-button secondary outline">
                            {t('medicalConsultations.healthForm.cancelPresignButton')}
                          </Button>
                        </Form.Item>
                        :
                        <Form.Item name="sign">
                          {pendingExams && !(resultState === 'pending' && allowPendingConsultationSign(medicalConsultationDetails?.pending_reasons))
                            ?
                            <Popover
                              title="Exámenes pendientes"
                              trigger="hover"
                              content={
                                <div>
                                  {
                                    unfinishedExams
                                      .map((unfinishedExam) => <p>{unfinishedExam.name}</p>)
                                  }
                                </div>
                              }
                              overlayClassName="sidebar-tooltip"
                            >
                              <Button
                                id="signButton"
                                htmlType="submit"
                                className="cmt-button secondary disabled"
                                type="primary"
                                size="large"
                                disabled
                                loading={loading.criterions}
                              >
                                {isCompanyAuditorSignerEnabled() ? "Firma auditor" : "Firmar"}
                              </Button>
                            </Popover>
                            :
                            <SignButton
                              countryCode={countryCode}
                              medicalFormType='health'
                              t={t}
                              showConfirmReprovingValues={showConfirmReprovingValues}
                              setShowConfirmReprovingValues={setShowConfirmReprovingValues}
                              loadingConsultation={loadingConsultation}
                              validateCompletedReport={validateCompletedReport}
                              isAuditorInfoEnabled={isAuditorInfoEnabled}
                              isCompanyAuditorSignerEnabled={isCompanyAuditorSignerEnabled}
                              form={form}
                              isUnableToChoose={isUnableToChoose}
                              approvedTextList={approvedTextList}
                              medicalConsultationDetails={medicalConsultationDetails}
                              roles={roles}
                              validatedExams={validatedExams}
                              servicesList={servicesList}
                              loading={loading}
                              generatedPrescriptions={generatedPrescriptions}
                              hasDeferredExamPrescription={hasDeferredExamPrescription}
                            />
                          }
                        </Form.Item>
                    }
                  </Col>
                }
              </Fragment>
            }
          </HasAccess>
          {
            (medicalConsultationDetails?.started_at && !pendingExams && medicalConsultationDetails?.state !== "signed") && (
              <Col xxl={1} lg={2} md={2} sm={2} xs={24}>
                <Tooltip
                  title="Previsualizar Informe"
                  color="#223A70"
                >
                  <Button
                    className="cmt-button secondary outline"
                    type="secondary"
                    icon={<EyeOutlined />}
                    size={"large"}
                    loading={loadingDownload}
                    onClick={() =>
                      downloadUnSignedReport(medicalConsultationDetails?.id)
                    }
                  />
                </Tooltip>
              </Col>
            )
          }
          {
            medicalConsultationDetails?.state === "signed" && (
              <Fragment>
                <Col xxl={1} lg={2} md={2} sm={2} xs={24}>
                  <Tooltip
                    title="Descargar Informe"
                    color="#223A70"
                  >
                    <Button
                      className="cmt-button secondary outline pl-5"
                      type="secondary"
                      icon={<span className="icon-system-download" />}
                      size={"large"}
                      loading={loadingDownload}
                      onClick={() =>
                        downloadSignedReport(
                          'download',
                          medicalConsultationDetails["id"],
                          "signed"
                        )
                      }
                    />
                  </Tooltip>
                </Col>
                <Col xxl={1} lg={2} md={2} sm={2} xs={24}>
                  <Tooltip
                    title="Ver Informe Firmado"
                    color="#223A70"
                  >
                    <Button
                      className="cmt-button secondary outline pl-5"
                      type="secondary"
                      icon={<EyeOutlined />}
                      size={"large"}
                      loading={loadingDownload}
                      onClick={() =>
                        downloadSignedReport(
                          'open',
                          medicalConsultationDetails["id"],
                          "signed"
                        )
                      }
                    />
                  </Tooltip>
                </Col>
              </Fragment>
            )
          }
        </Row >
      </Form >

      <ConclusionsModal
        recommendatiosModal={recommendatiosModal}
      />
      <ImportAnamnesisModal
        importAnamnesisModal={importAnamnesisModal}
      />
    </Fragment >
  )
};

export default Health;